import type { HttpClient } from '@adapters/HttpClient';
import type {
  AdvancedFiltersParam,
  NotificationPreferencesQueryParams,
  NotificationPreferencesResponse,
  NotificationReadFilter,
  NotificationsResponse,
  UpdatePreferenceRequest,
} from '../models/Notification.types';

interface GetNotificationsParams {
  page?: number;
  per_page?: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: AdvancedFiltersParam;
}

export class NotificationsApiAdapter {
  constructor(private httpClient: HttpClient) { }

  async getNotifications(params: GetNotificationsParams = {}): Promise<NotificationsResponse> {
    const queryParams = this.buildQueryParams(params);
    return this.httpClient.get<NotificationsResponse>(`/notifications${queryParams}`);
  }

  async getUnreadCount(): Promise<number> {
    // TODO: Replace with dedicated endpoint GET /notifications/unread-count when available
    // Current workaround: fetch with read_at IS NULL filter and use meta.total
    const response = await this.httpClient.get<NotificationsResponse>(
      '/notifications?per_page=1&advanced_filters[filters][0][column]=read_at&advanced_filters[filters][0][operator]=is_null&advanced_filters[logic]=AND',
    );
    return response.meta.total;
  }

  async markAsRead(notificationId: number): Promise<void> {
    await this.httpClient.patch(`/notifications/${notificationId}/read`, {});
  }

  async archiveNotification(notificationId: number): Promise<void> {
    await this.httpClient.patch(`/notifications/${notificationId}/archive`, {});
  }

  /**
   * GET /api/v1/notification/preferences
   * Obtiene las preferencias de notificación del usuario con soporte de paginación y filtros.
   */
  async getPreferences(params: NotificationPreferencesQueryParams = {}): Promise<NotificationPreferencesResponse> {
    const queryParams = this.buildPreferencesQueryParams(params);
    return this.httpClient.get<NotificationPreferencesResponse>(`/notification/preferences${queryParams}`);
  }

  private buildPreferencesQueryParams(params: NotificationPreferencesQueryParams): string {
    const queryParts: string[] = [];

    if (params.per_page != null) {
      queryParts.push(`per_page=${params.per_page}`);
    }
    if (params.page != null) {
      queryParts.push(`page=${params.page}`);
    }
    if (params['sorting[column]']) {
      queryParts.push(`sorting[column]=${encodeURIComponent(params['sorting[column]']!)}`);
    }
    if (params['sorting[direction]']) {
      queryParts.push(`sorting[direction]=${params['sorting[direction]']}`);
    }
    if (params.advanced_filters?.filters && params.advanced_filters.filters.length > 0) {
      params.advanced_filters.filters.forEach((filter, index) => {
        queryParts.push(`advanced_filters[filters][${index}][column]=${encodeURIComponent(filter.column)}`);
        queryParts.push(`advanced_filters[filters][${index}][operator]=${encodeURIComponent(filter.operator)}`);
        if (filter.value !== undefined) {
          if (Array.isArray(filter.value)) {
            filter.value.forEach((val, valIndex) => {
              queryParts.push(`advanced_filters[filters][${index}][value][${valIndex}]=${encodeURIComponent(val)}`);
            });
          } else {
            queryParts.push(`advanced_filters[filters][${index}][value]=${encodeURIComponent(filter.value)}`);
          }
        }
      });
      queryParts.push(`advanced_filters[logic]=${params.advanced_filters.logic || 'AND'}`);
    }

    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  async updatePreference(data: UpdatePreferenceRequest): Promise<void> {
    await this.httpClient.patch('/notification/preferences', data);
  }

  private buildQueryParams(params: GetNotificationsParams): string {
    const queryParts: string[] = [];

    if (params.per_page) {
      queryParts.push(`per_page=${params.per_page}`);
    }

    if (params.page) {
      queryParts.push(`page=${params.page}`);
    }

    if (params.sortColumn) {
      queryParts.push(`sorting[column]=${params.sortColumn}`);
    }

    if (params.sortDirection) {
      queryParts.push(`sorting[direction]=${params.sortDirection}`);
    }

    // Build advanced_filters in bracket format
    if (params.filters?.filters && params.filters.filters.length > 0) {
      params.filters.filters.forEach((filter, index) => {
        queryParts.push(`advanced_filters[filters][${index}][column]=${encodeURIComponent(filter.column)}`);
        queryParts.push(`advanced_filters[filters][${index}][operator]=${encodeURIComponent(filter.operator)}`);
        if (filter.value !== undefined) {
          if (Array.isArray(filter.value)) {
            filter.value.forEach((val, valIndex) => {
              queryParts.push(`advanced_filters[filters][${index}][value][${valIndex}]=${encodeURIComponent(val)}`);
            });
          } else {
            queryParts.push(`advanced_filters[filters][${index}][value]=${encodeURIComponent(filter.value)}`);
          }
        }
      });
      queryParts.push(`advanced_filters[logic]=${params.filters.logic || 'AND'}`);
    }

    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  /**
   * Filtros enviados al API. No mezclar con `archived_at` aquí: el backend no siempre
   * lo soporta en advanced_filters y puede devolver lista vacía. Las archivadas se
   * excluyen en cliente (useNotifications / useUnreadCount).
   */
  buildFiltersForReadFilter(filter: NotificationReadFilter): AdvancedFiltersParam | undefined {
    switch (filter) {
      case 'unread':
        return {
          logic: 'AND',
          filters: [{ column: 'read_at', operator: 'is_null' }],
        };
      case 'read':
        return {
          logic: 'AND',
          filters: [{ column: 'read_at', operator: 'is_not_null' }],
        };
      default:
        return undefined;
    }
  }
}
